/**
 * Simplified layout for seller (sub-user) accounts.
 *
 * Mobile-first: top bar with a hamburger button (right side) that opens a
 * Sheet drawer containing all nav links + the logout action. The drawer
 * closes automatically when a link is tapped. No bottom navigation —
 * previous bottom-nav was getting clipped by mobile browser chrome and
 * iOS home-indicator area.
 */
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { api } from "../../App";
import { ShoppingCart, History, LogOut, ShoppingBag, Users, Package, Menu, Wallet } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetHeader } from "../../components/ui/sheet";

const NAV_ITEMS = [
  { to: "/seller/take",        label: "Tomar pedido",      testid: "seller-nav-take",        Icon: ShoppingCart },
  { to: "/seller/orders",      label: "Mis pedidos",       testid: "seller-nav-orders",      Icon: History },
  { to: "/seller/customers",   label: "Clientes",          testid: "seller-nav-customers",   Icon: Users },
  { to: "/seller/products",    label: "Catálogo",          testid: "seller-nav-products",    Icon: Package },
  { to: "/seller/receivables", label: "Cuentas por cobrar", testid: "seller-nav-receivables", Icon: Wallet },
];

export default function SellerLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [me, setMe] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        setMe(data);
        // Guard: if somehow an admin lands here, bounce to admin app.
        if (data.role !== "seller") navigate("/", { replace: true });
      } catch {
        navigate("/login", { replace: true });
      }
    })();
  }, [navigate]);

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    navigate("/login", { replace: true });
  };

  // Best-effort: derive the title for the topbar from the current path so the
  // seller always knows which screen they're on without needing the menu open.
  const activeItem = NAV_ITEMS.find(n => location.pathname.startsWith(n.to));

  const itemClass = ({ isActive }) =>
    `flex items-center gap-3 px-4 py-3 rounded-sm text-base transition-colors ${
      isActive
        ? "bg-[#EEF2FF] text-[#002FA7] font-semibold"
        : "text-slate-700 hover:bg-slate-100"
    }`;

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-20">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <div className="h-9 w-9 rounded-sm bg-[#002FA7] text-white flex items-center justify-center font-bold shrink-0">
              <ShoppingBag className="h-4 w-4" />
            </div>
            <div className="min-w-0">
              <p
                className="text-sm font-bold text-slate-900 truncate"
                style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}
              >
                {activeItem?.label || me?.display_name || me?.name || "Vendedor"}
              </p>
              <p className="text-[11px] text-slate-500 truncate">
                {me?.display_name || me?.name || me?.email || ""}
              </p>
            </div>
          </div>

          <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
            <SheetTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                aria-label="Abrir menú"
                data-testid="seller-menu-button"
                className="rounded-sm shrink-0"
              >
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent
              side="right"
              className="w-[85vw] sm:w-[360px] p-0 flex flex-col bg-white"
              data-testid="seller-menu-drawer"
            >
              <SheetHeader className="px-4 py-4 border-b border-slate-200 text-left">
                <SheetTitle
                  className="text-lg font-black"
                  style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}
                >
                  {me?.display_name || me?.name || "Vendedor"}
                </SheetTitle>
                <p className="text-xs text-slate-500 truncate">{me?.email}</p>
              </SheetHeader>

              <nav className="flex-1 overflow-y-auto p-3 space-y-1">
                {NAV_ITEMS.map(({ to, label, testid, Icon }) => (
                  <NavLink
                    key={to}
                    to={to}
                    onClick={() => setMenuOpen(false)}
                    className={itemClass}
                    data-testid={testid}
                  >
                    <Icon className="h-5 w-5 shrink-0" />
                    <span>{label}</span>
                  </NavLink>
                ))}
              </nav>

              <div className="border-t border-slate-200 p-3">
                <Button
                  variant="outline"
                  onClick={logout}
                  data-testid="seller-logout-button"
                  className="w-full justify-center rounded-sm text-rose-700 border-rose-200 hover:bg-rose-50 hover:text-rose-800"
                >
                  <LogOut className="h-4 w-4 mr-2" />
                  Cerrar sesión
                </Button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </header>

      <main className="flex-1 max-w-3xl mx-auto w-full px-4 py-6 pb-10">
        <Outlet />
      </main>
    </div>
  );
}
